import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy } from "@angular/core";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngxs/store";
import * as ApplicationStateActions from "@vp/data-access/application";
import { OrganizationState } from "@vp/data-access/organization";
import * as UserStateActions from "@vp/data-access/users";
import { UserFilter, UserState, UserStateService } from "@vp/data-access/users";
import { Organization, OrganizationFeatures, PageState } from "@vp/models";
import { TagSelectorContext } from "@vp/shared/components/tag-selector";
import { FeatureService } from "@vp/shared/features";
import { PermissionsConstService } from "@vp/shared/permissions-const";
import Fuse from "fuse.js";
import { BehaviorSubject, combineLatest, map, Observable, of, Subject, switchMap, tap } from "rxjs";

export interface FlattenedUser {
  userId: string;
  userName: string;
  userEmail: string;
  userRoles: string;
  userTags: string;
  active: string;
  userLastLoginDate: string;
}

@Component({
  selector: "vp-users-list",
  standalone: false,
  templateUrl: "./users-list.component.html",
  styleUrls: ["./users-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TagSelectorContext, UserStateService]
})
export class UsersListComponent implements OnDestroy {
  organization$: Observable<Organization | null> = inject(Store).select(
    OrganizationState.organization
  );
  currentFilter$: Observable<UserFilter> = inject(Store).select(UserState.currentFilter);
  pageState$: Observable<Partial<PageState>> = inject(Store).select(UserState.pageState);

  @Input() columnsToDisplay!: string[];
  @Input() isRouterLinkEnabled!: boolean;

  @Input() set users(value: FlattenedUser[]) {
    this.flattenedUsers$.next(value);
  }

  private readonly _destroyed$ = new Subject<void>();
  readonly assignedUsers$ = new BehaviorSubject<FlattenedUser[]>([]);
  readonly flattenedUsers$ = new BehaviorSubject<FlattenedUser[]>([]);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly featureService: FeatureService,
    public permConst: PermissionsConstService,
    private readonly store: Store,
    private router: Router
  ) {}

  advancedSearchEnabled$ = this.featureService.featureFlagEnabled$(
    OrganizationFeatures.userAdministration,
    "advancedSearch"
  );

  search$ = this.activatedRoute.queryParamMap.pipe(map(params => params.get("search") ?? ""));

  fuse$ = this.flattenedUsers$.pipe(
    map(users => {
      return new Fuse(users, {
        includeMatches: true,
        keys: ["userName", "userEmail", "userRoles", "userTags", "active", "userLastLoginDate"],
        threshold: 0, //require exact match
        ignoreLocation: true,
        useTokenSearch: true,
        shouldSort: false //keep our sorting
      });
    })
  );

  results$ = this.advancedSearchEnabled$.pipe(
    switchMap(advancedSearchEnabled => {
      if (!advancedSearchEnabled) {
        return of([]);
      }
      return combineLatest([this.fuse$, this.search$]).pipe(
        map(([fuse, search]) => {
          if (!search) {
            return {
              search: "",
              results: fuse.getIndex().docs.map(user => ({
                user,
                matches: []
              }))
            };
          }

          const recommendation =
            (this.store.selectSnapshot(UserState.metaData)?.["recommendation"] as string) ?? "";

          if (recommendation != search) {
            this.store.dispatch(
              new ApplicationStateActions.SetSearchRecommendation(recommendation)
            );
          }

          return {
            search,
            results: fuse.search(search).map(r => ({
              user: r.item,
              matches: r.matches
            }))
          };
        }),
        tap(({ search, results }) => {
          const matches = Array.from(
            new Set(
              results
                .flatMap(r => r.matches ?? [])
                .flatMap(m => {
                  if (!m.value) {
                    return [];
                  }

                  return [m.value.trim()];
                })
                .filter(match => match.toLowerCase() !== search.toLowerCase())
            )
          ).slice(0, 5);

          this.store.dispatch(new ApplicationStateActions.SetSearchSuggestions(matches));
        }),
        map(({ results }) => results)
      );
    })
  );

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  sortChangeHandler($event: Sort) {
    this.store.dispatch(
      new UserStateActions.SetFilter({
        sort: $event.active,
        sortDirection: $event.direction
      })
    );
  }

  editUser(userId: string) {
    this.router.navigate(["/administration/user", userId], {
      queryParams: { returnUrl: "users" }
    });
  }
}
